interface QueuedRequest<T> {
  id: string;
  fn: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  retryCount: number;
  lastAttempt: number;
}

interface ThrottleConfig {
  maxConcurrent: number;
  delayBetweenBatches: number;
  maxRetries: number;
  baseRetryDelay: number;
}

class APIThrottler {
  private queue: QueuedRequest<any>[] = [];
  private running: Set<string> = new Set();
  private config: ThrottleConfig;

  constructor(config: Partial<ThrottleConfig> = {}) {
    this.config = {
      maxConcurrent: 3,
      delayBetweenBatches: 100,
      maxRetries: 3,
      baseRetryDelay: 1000,
      ...config
    };
  }

  async addRequest<T>(id: string, fn: () => Promise<T>): Promise<T> {
    // Check if this request is already running or queued
    const existingRequest = this.queue.find(req => req.id === id);
    if (existingRequest || this.running.has(id)) {
      // Return a promise that resolves when the existing request completes
      return new Promise((resolve, reject) => {
        const checkCompletion = () => {
          const queuedReq = this.queue.find(req => req.id === id);
          if (!queuedReq && !this.running.has(id)) {
            // Request completed, but we need to re-add it
            this.addRequest(id, fn).then(resolve).catch(reject);
          } else {
            setTimeout(checkCompletion, 50);
          }
        };
        checkCompletion();
      });
    }

    return new Promise((resolve, reject) => {
      const request: QueuedRequest<T> = {
        id,
        fn,
        resolve,
        reject,
        retryCount: 0,
        lastAttempt: 0
      };

      this.queue.push(request);
      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.running.size >= this.config.maxConcurrent || this.queue.length === 0) {
      return;
    }

    const request = this.queue.shift();
    if (!request) return;

    this.running.add(request.id);

    try {
      const result = await request.fn();
      request.resolve(result);
    } catch (error) {
      await this.handleError(request, error as Error);
    } finally {
      this.running.delete(request.id);
      
      // Add delay between batches to prevent overwhelming the API
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), this.config.delayBetweenBatches);
      } else {
        // Process immediately if queue is not empty and we have capacity
        this.processQueue();
      }
    }
  }

  private async handleError(request: QueuedRequest<any>, error: Error): Promise<void> {
    const now = Date.now();
    const timeSinceLastAttempt = now - request.lastAttempt;
    
    // Check if this is a rate limiting error (500 status or specific error messages)
    const isRateLimited = error.message.includes('Failed to fetch') || 
                         error.message.includes('500') ||
                         error.message.includes('rate limit');

    if (request.retryCount < this.config.maxRetries && isRateLimited) {
      request.retryCount++;
      request.lastAttempt = now;
      
      // Exponential backoff: baseDelay * (2 ^ retryCount) + random jitter
      const backoffDelay = this.config.baseRetryDelay * Math.pow(2, request.retryCount - 1);
      const jitter = Math.random() * 1000; // 0-1000ms jitter
      const totalDelay = backoffDelay + jitter;

      console.log(`Retrying request ${request.id} in ${Math.round(totalDelay)}ms (attempt ${request.retryCount}/${this.config.maxRetries})`);

      setTimeout(() => {
        this.queue.unshift(request); // Add back to front of queue
        this.processQueue();
      }, totalDelay);
    } else {
      // Max retries reached or non-retryable error
      console.error(`Request ${request.id} failed after ${request.retryCount} retries:`, error);
      request.reject(error);
    }
  }

  getQueueStatus() {
    return {
      queueLength: this.queue.length,
      runningCount: this.running.size,
      isIdle: this.queue.length === 0 && this.running.size === 0
    };
  }

  clear() {
    this.queue.forEach(req => req.reject(new Error('Queue cleared')));
    this.queue = [];
    this.running.clear();
  }
}

// Global throttler instance for device details
export const deviceDetailsThrottler = new APIThrottler({
  maxConcurrent: 3,
  delayBetweenBatches: 150,
  maxRetries: 3,
  baseRetryDelay: 1000
});

// Global throttler instance for device updates (more lenient)
export const deviceUpdateThrottler = new APIThrottler({
  maxConcurrent: 2,
  delayBetweenBatches: 200,
  maxRetries: 2,
  baseRetryDelay: 1500
});